// Andón de cocina dentro del ERP (Catering → Piso de cocina → Andón).
// Muestra la vista de pared y las URL con token para las TV colgadas (sin login).
import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, ExternalLink, Loader2, Maximize2, Minimize2, MonitorPlay } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { fieldCls } from '@/features/inventario/shared';
import { AndonVista } from './andonVista';
import { Estacion, PisoPantalla, PisoTablero, cat, catRpc } from './shared';

export function AndonCatering() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [estacion, setEstacion] = useState('');
  const [pleno, setPleno] = useState(false);

  const { data: t } = useQuery<PisoTablero>({
    queryKey: ['cat-piso', 'andon', estacion],
    queryFn: () => catRpc<PisoTablero>('piso_tablero', { _fecha: null, _estacion: estacion || null }),
    refetchInterval: 15000, staleTime: 0, refetchOnMount: 'always',
  });
  const { data: estaciones = [] } = useQuery<Estacion[]>({
    queryKey: ['cat-estaciones'],
    queryFn: () => cat.list<Estacion>('estaciones', '?select=*&deleted_at=is.null&order=orden'),
  });
  const { data: pantallas = [] } = useQuery<PisoPantalla[]>({
    queryKey: ['cat-piso-pantallas'],
    queryFn: () => cat.list<PisoPantalla>('piso_pantalla', '?select=*&deleted_at=is.null&order=nombre'),
  });

  const urlDe = (token: string) => `${window.location.origin}/cocina/${token}`;

  const alternarPantallaCompleta = () => {
    if (document.fullscreenElement) { void document.exitFullscreen(); setPleno(false); }
    else { void rootRef.current?.requestFullscreen?.(); setPleno(true); }
  };

  return (
    <div ref={rootRef} className="space-y-4 p-4 bg-background min-h-full">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <MonitorPlay className="w-5 h-5" /> Andón de cocina
        </h1>
        {!pleno && (
          <select className={`${fieldCls} !w-auto`} value={estacion} onChange={(e) => setEstacion(e.target.value)}>
            <option value="">Andón general (todas las estaciones)</option>
            {estaciones.map((e) => <option key={e.id} value={e.codigo}>{e.nombre}</option>)}
          </select>
        )}
        <Button size="sm" onClick={alternarPantallaCompleta}>
          {pleno ? <Minimize2 className="mr-1 h-4 w-4" /> : <Maximize2 className="mr-1 h-4 w-4" />} Pantalla completa
        </Button>
      </div>

      {/* Vista previa del tablero de pared */}
      <div className="overflow-hidden rounded-xl border">
        {t ? <AndonVista t={t} titulo={estaciones.find((e) => e.codigo === estacion)?.nombre} />
           : <div className="flex items-center gap-2 p-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Cargando tablero…</div>}
      </div>

      {!pleno && (
      <>
      {/* TV colgadas: URL pública por token, sin login */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Pantallas de cocina (TV sin login)</CardTitle>
          <p className="text-sm text-muted-foreground">
            Abra esta dirección en la TV o el Android TV box de la cocina: entra directo al andón por token,
            sin usuario ni contraseña, y se refresca solo.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {pantallas.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm">
              <span className="font-medium">{p.nombre}</span>
              {p.estacion_id && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                  {estaciones.find((e) => e.id === p.estacion_id)?.nombre ?? 'estación'}
                </span>
              )}
              <code className="flex-1 truncate text-xs text-muted-foreground">{urlDe(p.token)}</code>
              <span className="text-xs text-muted-foreground">
                {p.last_seen ? `visto ${new Date(p.last_seen).toLocaleString()}` : 'nunca conectada'}
              </span>
              <Button size="sm" variant="outline" onClick={() => {
                void navigator.clipboard.writeText(urlDe(p.token));
                toast.success('Dirección copiada');
              }}><Copy className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" onClick={() => window.open(urlDe(p.token), '_blank')}>
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          {!pantallas.length && (
            <p className="text-sm text-muted-foreground">
              Sin pantallas registradas. Créelas en Estaciones y equipos → Pantallas del andón.
            </p>
          )}
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}
